import { useMutation } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { ApiPath } from "@/api/path";

export type MeasurementClassification = 'too_high' | 'too_low' | 'unknown' | 'normal';

export type TemperatureMeasurement = {
  type: 'min' | 'p5' | 'mean' | 'median' | 'p95' | 'max';
  value: number;
  classification: MeasurementClassification;
}

export type PredictedRange = {
  min: number;
  max: number;
}

export type SensorAnalysis = {
  sensor: string;
  predicted_range: PredictedRange;
  measurements: TemperatureMeasurement[];
}

export type OnPeriodTemperatureAnomaliesResponse = {
  start: string;
  stop: string;
  sensors: SensorAnalysis[];
}

export type OnPeriodTemperatureAnomaliesParams = {
  installation_id: number;
  start: string;
  stop: string;
  window_size?: string;
}

/**
 * Hook that analyzes temperature anomalies for a specific on period
 * 
 * @returns Mutation function to analyze temperature anomalies for an on period
 */
const useOnPeriodTemperatureAnomalies = () => {
  return useMutation<
    OnPeriodTemperatureAnomaliesResponse, 
    Error, 
    OnPeriodTemperatureAnomaliesParams
  >({
    mutationFn: async ({ installation_id, start, stop, window_size }) => {
      const response = await apiClient.post(
        ApiPath.INSTALLATION_REFRIGERANT_ANALYZE_ON_PERIOD_TEMPERATURE_ANOMALIES,
        {
          installation_id: [installation_id],
          start,
          stop,
          window_size,
          timezone_for_bucketing: Intl.DateTimeFormat().resolvedOptions().timeZone
        }
      );
      return response.data;
    }
  });
};

export default useOnPeriodTemperatureAnomalies;